/**
 * Global Variant Sorting Component
 * Sorts product variant cards globally across all products in a collection
 * 
 * Usage:
 * - Requires variant cards wrapped in .variant-card-wrapper with data attributes:
 *   - data-sort-price: variant price in cents
 *   - data-sort-title: variant title
 *   - data-sort-created: variant created date
 * - Requires sort data element with id="variant-sort-data-{sectionId}" and data-sort-by attribute
 * - Requires product list with id="product-list-{sectionId}"
 */

(function() {
  'use strict';

  /**
   * Sort variant cards globally based on sort option
   * @param {string} sectionId - Section ID to identify elements
   */
  function sortVariantCards(sectionId) {
    try {
      const sortDataEl = document.getElementById(`variant-sort-data-${sectionId}`);
      if (!sortDataEl) {
        return;
      }
      
      // Get current sort from URL or data attribute
      const urlParams = new URLSearchParams(window.location.search);
      const sortBy = urlParams.get('sort_by') || sortDataEl.getAttribute('data-sort-by');
      if (!sortBy) {
        return;
      }
      
      const productList = document.getElementById(`product-list-${sectionId}`);
      if (!productList) {
        return;
      }
      
      // Only sort if sort option requires global sorting
      const needsGlobalSort = [
        'price-ascending', 
        'price-descending', 
        'title-ascending', 
        'title-descending', 
        'created-ascending', 
        'created-descending'
      ].includes(sortBy);
      
      if (!needsGlobalSort) {
        return;
      }
      
      // Collect all variant card wrappers
      const variantWrappers = Array.from(productList.querySelectorAll('.variant-card-wrapper'));
      if (variantWrappers.length === 0) {
        return;
      }
      
      // Sort wrappers based on sort option
      variantWrappers.sort((a, b) => {
        if (sortBy === 'price-ascending') {
          const priceA = parseInt(a.getAttribute('data-sort-price') || '0');
          const priceB = parseInt(b.getAttribute('data-sort-price') || '0');
          return priceA - priceB;
        } else if (sortBy === 'price-descending') {
          const priceA = parseInt(a.getAttribute('data-sort-price') || '0');
          const priceB = parseInt(b.getAttribute('data-sort-price') || '0');
          return priceB - priceA;
        } else if (sortBy === 'title-ascending') {
          const titleA = (a.getAttribute('data-sort-title') || '').toLowerCase();
          const titleB = (b.getAttribute('data-sort-title') || '').toLowerCase();
          return titleA.localeCompare(titleB);
        } else if (sortBy === 'title-descending') {
          const titleA = (a.getAttribute('data-sort-title') || '').toLowerCase();
          const titleB = (b.getAttribute('data-sort-title') || '').toLowerCase();
          return titleB.localeCompare(titleA);
        } else if (sortBy === 'created-ascending') {
          const createdA = a.getAttribute('data-sort-created') || '';
          const createdB = b.getAttribute('data-sort-created') || '';
          return createdA.localeCompare(createdB);
        } else if (sortBy === 'created-descending') {
          const createdA = a.getAttribute('data-sort-created') || '';
          const createdB = b.getAttribute('data-sort-created') || '';
          return createdB.localeCompare(createdA);
        }
        return 0;
      });
      
      // Re-append sorted wrappers (remove first, then append in order)
      variantWrappers.forEach(wrapper => {
        wrapper.remove();
      });
      variantWrappers.forEach(wrapper => {
        productList.appendChild(wrapper);
      });
      
      // Update reveal delays for animation
      variantWrappers.forEach((wrapper, index) => {
        const card = wrapper.querySelector('.product-card');
        if (card) {
          card.setAttribute('data-reveal-delay', (index + 1) * 50);
        }
      });
      
      // Update the data-sort-by attribute to keep it in sync with URL
      sortDataEl.setAttribute('data-sort-by', sortBy);
    } catch (error) {
      console.error('[Variant Sort] Error sorting cards for section:', sectionId, error);
    }
  }

  /**
   * Initialize sorting for a specific section
   * @param {string} sectionId - Section ID
   * @param {number} delay - Delay in milliseconds (default: 100)
   */
  function initVariantSort(sectionId, delay = 100) {
    setTimeout(() => sortVariantCards(sectionId), delay);
  }

  /**
   * Initialize all variant sorting on page load
   */
  function initAllVariantSorts() {
    // Find all sort data elements and initialize sorting for each
    const sortDataElements = document.querySelectorAll('[id^="variant-sort-data-"]');
    sortDataElements.forEach(el => {
      const sectionId = el.id.replace('variant-sort-data-', '');
      initVariantSort(sectionId, 300); // Longer delay for initial load
    });
  }

  /**
   * Re-sort when collection updates (AJAX)
   */
  function handleCollectionUpdate() {
    // Wait a bit longer for DOM to fully update after AJAX
    setTimeout(() => {
      const sortDataElements = document.querySelectorAll('[id^="variant-sort-data-"]');
      sortDataElements.forEach(el => {
        const sectionId = el.id.replace('variant-sort-data-', '');
        sortVariantCards(sectionId);
      });
    }, 300);
  }

  // Initialize on page load - run immediately if DOM is ready, otherwise wait
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(initAllVariantSorts, 100);
    });
  } else {
    // DOM is already ready, but wait a bit for any dynamic content
    setTimeout(initAllVariantSorts, 200);
  }

  // Re-sort when collection updates (AJAX)
  // Listen to both events - custom.js dispatches 'facet:update', theme.js may dispatch 'facet:updated'
  document.addEventListener('facet:update', handleCollectionUpdate);
  document.addEventListener('facet:updated', handleCollectionUpdate);
  document.addEventListener('shopify:section:load', handleCollectionUpdate);

  // Also watch for new variant-sort-data elements being added (for AJAX updates)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          // Check if the added node is a variant-sort-data element
          if (node.id && node.id.startsWith('variant-sort-data-')) {
            const sectionId = node.id.replace('variant-sort-data-', '');
            setTimeout(() => sortVariantCards(sectionId), 300);
          }
          // Check if the added node contains variant-sort-data elements
          const sortDataElements = node.querySelectorAll && node.querySelectorAll('[id^="variant-sort-data-"]');
          if (sortDataElements && sortDataElements.length > 0) {
            sortDataElements.forEach(el => {
              const sectionId = el.id.replace('variant-sort-data-', '');
              setTimeout(() => sortVariantCards(sectionId), 300);
            });
          }
        }
      });
    });
  });

  // Start observing the document body for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Export for manual initialization if needed
  window.VariantSort = {
    sort: sortVariantCards,
    init: initVariantSort
  };

})();

